import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientServices, ClientService } from '../../client-services/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface ServicesExpiring90DaysProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const now = new Date();
const ninetyDaysFromNow = new Date();
ninetyDaysFromNow.setDate(now.getDate() + 90);

const ServicesExpiring90Days: React.FC<ServicesExpiring90DaysProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { services, isLoading } = useClientServices();

  const items = React.useMemo(() => {
    const arr = Array.isArray(services) ? services : [];
    return arr
      .filter((s: ClientService) => {
        if (!s.serviceEndDate || !s.serviceActive) return false;
        const end = new Date(s.serviceEndDate);
        return end >= now && end <= ninetyDaysFromNow;
      })
      .sort((a: ClientService, b: ClientService) => {
        const da = new Date(a.serviceEndDate || 0).getTime();
        const db = new Date(b.serviceEndDate || 0).getTime();
        return da - db;
      })
      .slice(0, effectiveLimit);
  }, [services, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  const getDays = (dateString?: string) => {
    if (!dateString) return 0;
    return Math.ceil(
      (new Date(dateString).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );
  };

  return (
    <GenericListWidget<ClientService>
      title="Services Expiring (90 Days)"
      viewAllPath="/client-services"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No services expiring in 90 days"
      getItemKey={(s) => s.clientServicetId}
      embedded={embedded}
      renderItem={(service) => {
        const days = getDays(service.serviceEndDate);
        const isUrgent = days <= 7;
        return (
          <div
            role="button"
            tabIndex={0}
            onClick={() =>
              navigate(`/client-services/${service.clientServicetId}`)
            }
            onKeyDown={(e) =>
              e.key === 'Enter' &&
              navigate(`/client-services/${service.clientServicetId}`)
            }
            className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
          >
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold ${
                isUrgent ? 'bg-red-500' : 'bg-amber-500'
              }`}
            >
              {days}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-xs sm:text-sm font-medium ${colors.text}`}>
                {service.client?.clientName || `Client #${service.clientId}`}
              </p>
              <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
                {service.serviceEndDate &&
                  new Date(service.serviceEndDate).toLocaleDateString('en-US')}
              </p>
            </div>
          </div>
        );
      }}
    />
  );
};

export default ServicesExpiring90Days;
